import { Flex, Spacer } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { ViewNonRecoverableError } from '@view/view_non_recoverable_error/view_non_recoverable_error.component';
import { Logger } from '@visiba/logger';
import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
	children?: ReactNode;
	fallback?: JSX.Element;
}

interface State {
	error: Error | null;
	errorInfo: ErrorInfo | null;
	fallback: JSX.Element | null;
	hasError: boolean;
}

function NonRecoverableError({ error, errorInfo }: { error: Error | null; errorInfo: ErrorInfo | null }): JSX.Element {
	return (
		<Flex grow={1}>
			<ViewNonRecoverableError />
			{import.meta.env.DEV ? (
				<details style={{ whiteSpace: 'pre-wrap' }}>
					{error && error.toString()}
					<Spacer vertical={tokens.spacing.x3} />
					{errorInfo?.componentStack}
				</details>
			) : null}
		</Flex>
	);
}

// ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️
//
// It's good to know that the error overlay will still show during development
// and to this day there's not good way to circumvent it.
// See: https://github.com/facebook/create-react-app/issues/6530
//
// ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️ ⚠️ 🚨 ⚠️ 🚨 ⚠️ 🚨 ⚠️

export class ErrorBoundary extends Component<Props, State> {
	private readonly logger = new Logger('error boundary');

	constructor(props: Props) {
		super(props);

		this.state = {
			error: null,
			errorInfo: null,
			fallback: props.fallback ?? null,
			hasError: false,
		};
	}

	public static getDerivedStateFromError(error: unknown): { hasError: boolean; error: unknown } {
		// Update state so the next render will show the fallback UI.
		return { hasError: true, error };
	}

	componentDidCatch(_error: unknown, errorInfo: ErrorInfo): void {
		if (_error instanceof Promise) {
			this.logger.logDebug('a promise was thrown, was the component not wrapped in suspense?');

			return;
		}

		let error: Error;
		let message: string;
		if (_error instanceof Error) {
			error = _error;
			message = _error.message;
		} else {
			message = `${_error}`;
			error = new Error(message);
		}

		// this(Logger.Level.error, message, {
		// 	stackTrace: errorInfo.componentStack,
		// 	data: {},
		// 	tags: [],
		// 	immediately: true,
		// });

		this.logger.logFatal('error in component: {error}', error);

		this.setState({
			errorInfo,
			error,
		});
	}

	render(): ReactNode {
		const { fallback, errorInfo, error, hasError } = this.state;

		if (hasError && fallback == null) {
			return <NonRecoverableError error={error} errorInfo={errorInfo} />;
		} else if (hasError && fallback !== null) {
			return fallback;
		}

		return this.props.children;
	}
}
